import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Close as CloseIcon,
  PersonAddAlt1Outlined as NewClientIcon,
  PhoneOutlined as PhoneIcon,
} from '@mui/icons-material';
import AlertDialog from '../../../components/AlertDialog';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { ComptoirClientInput, ComptoirClientOption } from '../../../types/comptoir';
import { COLOR, ClientAvatar, digits, phoneValid, primaryBtnSx } from '../ui';

type Duplicate = { id: number; nom: string; surnom?: string | null } | null;

interface ClientFormDialogProps {
  open: boolean;
  client?: ComptoirClientOption | null; // set = edit
  initial?: Partial<ComptoirClientInput>; // prefill for a new client (e.g. what was typed in a search)
  onClose: () => void;
  save: (data: ComptoirClientInput) => Promise<ComptoirClientOption>;
  onSaved: (c: ComptoirClientOption) => void;
  /** Active client with these phone digits (other than the one being edited), or null. */
  findDuplicate: (tel: string) => Duplicate | Promise<Duplicate>;
  /** Offered when the phone already belongs to someone (e.g. "Utiliser ce client" in the wizard). */
  onUseExisting?: (id: number) => void;
}

/** Create / edit a Comptoir client — shared by the Clients page and the offre wizard. */
const ClientFormDialog: React.FC<ClientFormDialogProps> = ({
  open, client, initial, onClose, save, onSaved, findDuplicate, onUseExisting,
}) => {
  const isEdit = !!client;
  const [form, setForm] = useState({ nom: '', surnom: '', telephone: '' });
  const [touched, setTouched] = useState<{ nom?: boolean; telephone?: boolean }>({});
  const [submitted, setSubmitted] = useState(false);
  const [dup, setDup] = useState<Duplicate>(null);
  const [checking, setChecking] = useState(false);
  const [serverErr, setServerErr] = useState<{ nom?: string; telephone?: string; other?: string }>({});
  const [saving, setSaving] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const start = useRef('');

  useEffect(() => {
    if (!open) return;
    const f = {
      nom: client?.nom ?? initial?.nom ?? '',
      surnom: client?.surnom ?? initial?.surnom ?? '',
      telephone: client?.telephone ?? initial?.telephone ?? '',
    };
    setForm(f);
    start.current = JSON.stringify(f);
    setTouched({});
    setSubmitted(false);
    setServerErr({});
    setDup(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, client]);

  // Live duplicate-phone check (digits only, like the server)
  useEffect(() => {
    if (!open || !phoneValid(form.telephone)) { setDup(null); return; }
    setChecking(true);
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const d = await findDuplicate(form.telephone);
        if (alive) setDup(d && d.id !== client?.id ? d : null);
      } catch {
        if (alive) setDup(null);
      } finally {
        if (alive) setChecking(false);
      }
    }, 300);
    return () => { alive = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.telephone, open, client]);

  const errors: { nom?: string; telephone?: string } = {};
  if (!form.nom.trim()) errors.nom = 'Le nom est obligatoire.';
  if (!form.telephone.trim()) errors.telephone = 'Le téléphone est obligatoire : il sert à retrouver le client.';
  else if (!phoneValid(form.telephone)) errors.telephone = 'Numéro invalide : 9 à 15 chiffres (ex : 06 12 34 56 78).';
  const show = (k: 'nom' | 'telephone') => serverErr[k] || (submitted || touched[k] ? errors[k] : undefined);

  const dirty = JSON.stringify(form) !== start.current;
  const requestClose = () => {
    if (saving) return;
    if (dirty) setConfirmClose(true);
    else onClose();
  };

  const handleSave = async () => {
    setSubmitted(true);
    setServerErr({});
    if (errors.nom || errors.telephone || dup || checking) return;
    setSaving(true);
    try {
      const saved = await save({ nom: form.nom.trim(), surnom: form.surnom.trim() || null, telephone: form.telephone.trim() });
      onSaved(saved);
    } catch (e: any) {
      const msg = extractErrorMessage(e);
      if (/numéro|téléphone/i.test(msg)) setServerErr({ telephone: msg });
      else if (/nom/i.test(msg)) setServerErr({ nom: msg });
      else setServerErr({ other: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={requestClose} maxWidth="xs" fullWidth aria-labelledby="comptoir-client-dialog-title" PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2, borderBottom: 1, borderColor: 'divider' }}>
          {isEdit ? <ClientAvatar name={form.nom || client!.nom} size={42} /> : (
            <Box sx={{ width: 42, height: 42, borderRadius: 2, bgcolor: alpha(COLOR, 0.12), color: COLOR, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <NewClientIcon />
            </Box>
          )}
          <Box sx={{ flex: 1 }}>
            <Typography id="comptoir-client-dialog-title" variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{isEdit ? 'Modifier le client' : 'Nouveau client'}</Typography>
            <Typography variant="body2" color="text.secondary">{isEdit ? client!.nom : 'Nom et téléphone suffisent'}</Typography>
          </Box>
          <IconButton onClick={requestClose} aria-label="Fermer"><CloseIcon /></IconButton>
        </Box>

        <DialogContent sx={{ pt: '20px !important' }}>
          {serverErr.other && <Alert severity="error" sx={{ mb: 2 }}>{serverErr.other}</Alert>}
          <Stack spacing={1}>
            <TextField
              label="Nom complet / Raison sociale" required fullWidth autoFocus value={form.nom}
              onChange={(e) => { setForm({ ...form, nom: e.target.value }); setServerErr({}); }}
              onBlur={() => setTouched((t) => ({ ...t, nom: true }))}
              error={!!show('nom')} helperText={show('nom') || ' '}
            />
            <TextField
              label="Surnom / Nom usuel" fullWidth placeholder="Facultatif" value={form.surnom}
              onChange={(e) => setForm({ ...form, surnom: e.target.value })}
              helperText="Le nom sous lequel on le connaît au comptoir"
            />
            <TextField
              label="Téléphone" required fullWidth placeholder="06 12 34 56 78" value={form.telephone}
              onChange={(e) => { setForm({ ...form, telephone: e.target.value.replace(/[^\d+\s.-]/g, '') }); setServerErr({}); }}
              onBlur={() => setTouched((t) => ({ ...t, telephone: true }))}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
              inputProps={{ inputMode: 'tel', maxLength: 20 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ fontSize: 20 }} /></InputAdornment>,
                endAdornment: checking ? <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment> : undefined,
              }}
              error={!!show('telephone') || !!dup}
              helperText={(dup && `Ce numéro appartient déjà à ${dup.nom}.`) || show('telephone') || 'Sert à retrouver le client'}
            />
            {dup && onUseExisting && digits(form.telephone) && (
              <Button variant="outlined" size="small" onClick={() => onUseExisting(dup.id)} sx={{ alignSelf: 'flex-start', borderColor: COLOR, color: COLOR }}>
                Utiliser {dup.nom}{dup.surnom ? ` « ${dup.surnom} »` : ''}
              </Button>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={requestClose} color="inherit" disabled={saving}>Annuler</Button>
          <Button
            variant="contained" onClick={handleSave} disabled={saving} sx={{ ...primaryBtnSx, minWidth: 150 }}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            {saving ? 'Enregistrement…' : isEdit ? 'Enregistrer' : 'Créer le client'}
          </Button>
        </DialogActions>
      </Dialog>

      <AlertDialog
        open={confirmClose}
        type="warning"
        title="Fermer sans enregistrer ?"
        message="Les informations saisies pour ce client seront perdues."
        confirmText="Fermer"
        cancelText="Continuer"
        showCancel
        onConfirm={onClose}
        onClose={() => setConfirmClose(false)}
      />
    </>
  );
};

export default ClientFormDialog;
